/**
 * Authentication Models
 * 
 * TypeScript interfaces and types for authentication-related data structures.
 * These models are used throughout the Swagger Auth library for type safety.
 * 
 * @module Models/Auth
 */

/**
 * Login credentials required for authentication
 */
export interface LoginCredentials {
    /** Username or email address */
    username: string;

    /** User password */
    password: string;
}

/**
 * Login request payload sent to the API
 */
export interface LoginRequest {
    /** Username or email address */
    username: string;

    /** User password (will be sent over HTTPS) */
    password: string;

    /** Optional: Remember me flag for extended session */
    rememberMe?: boolean;
}

/**
 * Login response received from the API after successful authentication
 */
export interface LoginResponse {
    /** User information */
    user: User;

    /** JWT access token */
    token: string;

    /** Token expiration timestamp */
    tokenExpires: string | Date;

    /** Optional: Refresh token for extended sessions */
    refreshToken?: string;
}

/**
 * User information returned after authentication
 */
export interface User {
    /** Unique user identifier */
    id?: string;

    /** Username */
    username: string;

    /** User's email address */
    email: string;

    /** User's display name */
    displayName?: string;

    /** User roles (e.g., 'Admin', 'Developer', 'Viewer') */
    roles: string[];

    /** Optional: User's tenant/organization ID */
    tenantId?: string;

    /** Optional: Additional user metadata */
    metadata?: Record<string, unknown>;
}

/**
 * Decoded JWT token payload
 */
export interface TokenPayload {
    /** Subject (user ID) */
    sub: string;

    /** Username */
    username: string;

    /** User email */
    email: string;

    /** User roles */
    roles: string[];

    /** Issuer */
    iss: string;

    /** Audience */
    aud: string;

    /** Expiration time (Unix timestamp) */
    exp: number;

    /** Issued at (Unix timestamp) */
    iat: number;

    /** Not before (Unix timestamp) */
    nbf?: number;

    /** JWT ID */
    jti?: string;

    /** Optional: Tenant ID */
    tenantId?: string;
}

/**
 * Authentication state stored in session/local storage
 */
export interface AuthState {
    /** Whether user is currently authenticated */
    isAuthenticated: boolean;

    /** Current user information */
    user: User | null;

    /** JWT access token */
    token: string | null;

    /** Token expiration timestamp */
    tokenExpires: Date | null;

    /** Optional: Refresh token */
    refreshToken?: string | null;
}

/**
 * Authentication error response from API
 */
export interface AuthError {
    /** Error code (e.g., 'INVALID_CREDENTIALS', 'TOKEN_EXPIRED') */
    code: string;

    /** Human-readable error message */
    message: string;

    /** Optional: Additional error details */
    details?: Record<string, unknown>;

    /** HTTP status code */
    statusCode?: number;
}

/**
 * Token validation result
 */
export interface TokenValidation {
    /** Whether the token is valid */
    isValid: boolean;

    /** Decoded token payload (if valid) */
    payload?: TokenPayload;

    /** Validation error message (if invalid) */
    error?: string;

    /** Whether the token is expired */
    isExpired?: boolean;
}

/**
 * User authorization check result
 */
export interface AuthorizationResult {
    /** Whether the user is authorized */
    isAuthorized: boolean;

    /** Reason for denial (if not authorized) */
    reason?: string;

    /** Required roles that are missing */
    missingRoles?: string[];
}

/**
 * Storage keys for authentication data
 */
export const AUTH_STORAGE_KEYS = {
    /** Session storage key for authentication state */
    AUTH_STATE: 'swagger_auth_state',

    /** Session storage key for JWT token */
    TOKEN: 'swagger_auth_token',

    /** Session storage key for user info */
    USER: 'swagger_auth_user',

    /** Cookie name for JWT token */
    TOKEN_COOKIE: 'swagger_auth_token',

    /** Local storage key for remember me */
    REMEMBER_ME: 'swagger_auth_remember_me'
} as const;

/**
 * Authentication event types
 */
export enum AuthEvent {
    /** User logged in successfully */
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',

    /** User logged out */
    LOGOUT = 'LOGOUT',

    /** Token refreshed */
    TOKEN_REFRESHED = 'TOKEN_REFRESHED',

    /** Token expired */
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',

    /** Authentication failed */
    AUTH_FAILED = 'AUTH_FAILED',

    /** Unauthorized access attempt */
    UNAUTHORIZED = 'UNAUTHORIZED'
}

/**
 * Type guard to check if an object is a valid User
 */
export function isUser(obj: unknown): obj is User {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'username' in obj &&
        'email' in obj &&
        'roles' in obj &&
        Array.isArray((obj as User).roles)
    );
}

/**
 * Type guard to check if an object is a valid LoginResponse
 */
export function isLoginResponse(obj: unknown): obj is LoginResponse {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'user' in obj &&
        'token' in obj &&
        'tokenExpires' in obj &&
        isUser((obj as LoginResponse).user)
    );
}

/**
 * Login form data structure
 */
export interface LoginFormData {
    /** Optional tenant/organization name */
    tenancyName?: string;

    /** Username or email */
    username: string;

    /** Password */
    password: string;

    /** Client ID for multi-tenant authentication */
    clientId?: string;
}

/**
 * Swagger login response from API
 */
export interface SwaggerLoginResponse {
    /** Authentication token */
    token: string;

    /** User information */
    user: {
        /** User ID */
        id?: string;

        /** Username */
        username: string;

        /** Email address */
        email: string;

        /** User roles */
        roles: string[];
    };
}

/**
 * Branding configuration for UI customization
 */
export interface BrandingSettings {
    /** Platform name for headers and titles */
    platformName?: string;

    /** Subtitle or tagline */
    subtitle?: string;

    /** Company name for footer */
    companyName?: string;

    /** URL to logo image */
    logoUrl?: string;
}

/**
 * Cookie settings for token storage
 */
export interface CookieSettings {
    /** Cookie name */
    name?: string;

    /** Cookie domain */
    domain?: string;

    /** Whether cookie should be secure (HTTPS only) */
    secure?: boolean;

    /** SameSite attribute */
    sameSite?: 'strict' | 'lax' | 'none';

    /** Cookie path */
    path?: string;
}

/**
 * Swagger authentication settings
 */
export interface SwaggerAuthSettings {
    /** Login page URL */
    loginUrl: string;

    /** Swagger UI URL */
    swaggerUrl: string;

    /** Hangfire dashboard URL (optional) */
    hangfireUrl?: string;

    /** Unauthorized page URL */
    unauthorizedUrl: string;

    /** Not found page URL */
    notFoundUrl: string;

    /** API login endpoint */
    apiLoginEndpoint: string;

    /** Required role for access */
    requiredRole: string;

    /** Client ID for authentication */
    clientId: string;

    /** Token expiration in hours */
    tokenExpirationHours: number;

    /** Branding configuration */
    branding: BrandingSettings;

    /** Cookie settings */
    cookieSettings: CookieSettings;

    // Additional properties for compatibility with extended service
    /** Base API URL (optional, for extended configurations) */
    apiUrl?: string;

    /** Login page URL (alias for loginUrl) */
    loginPageUrl?: string;

    /** Authentication endpoint path */
    authEndpoint?: string;

    /** Logout endpoint path */
    logoutEndpoint?: string;

    /** Token expiration buffer in minutes */
    tokenExpirationBuffer?: number;

    /** Auto refresh tokens before expiration */
    autoRefreshToken?: boolean;

    /** Show notifications for auth events */
    showNotifications?: boolean;

    /** Enable debug logging */
    debug?: boolean;
}
